import React, { useState, useEffect } from 'react';
import { IChallengeService, ChangeChallengeParams } from '../../../model/interfaces';
import MainButton from '@ui-kit/components/mainButton';
import ChallengeDatePicker from '../challengeDatePicker/challengeDatePicker';
import { Moment } from 'moment';
import { useAsyncData } from '@utils/hooks';
import Empty from '@ui-kit/components/empty';
import { ReactComponent as Swords } from '../../../assets/swords.svg';
import ChallengeChat from '../challengeChat/challengeChat';
import { Loader } from '@ui-kit/components/loader';
import styles from './challengeDetails.module.scss';
import { t } from '@base/utils/helpers';

export default function ChallengeDetait(props: {
  id: string;
  challengeService: IChallengeService;
  editable: boolean;
  canSendMessages: boolean;
}) {
  const [changingDate, setChangingDate] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [date, setDate] = useState<Moment>();
  const { data: challenge, loading, setData: setChallenge } = useAsyncData(() =>
    props.challengeService.getChallenge(props.id)
  );
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!challenge) return;
    setDate(challenge.meetTime);
  }, [challenge]);

  const handleWithdraw = async () => {
    if (!challenge) return;
    try {
      await props.challengeService.withdrawChallenge(challenge.id);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    async function updateChallenge() {
      const updatedChallenge = await props.challengeService.getChallenge(props.id);
      setChallenge(updatedChallenge);
    }
    const interval = setInterval(updateChallenge, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMessage = async (message: string) => {
    if (!challenge) return;
    const res = await props.challengeService.sendMessage(challenge.id, message);
    setChallenge(res);
  };

  const handleChangeDate = async () => {
    if (!date || !challenge) return;
    const params: ChangeChallengeParams = {
      chid: challenge.id,
      day: date.format('YYYY MMM DD'),
      hour: date.format('hh'),
      ampm: date.format('A'),
      minute: date.format('mm'),
    };
    if (updating) return;
    try {
      setUpdating(true);
      await props.challengeService.changeChallengeDate(params);
    } catch (error) {
      console.error(error);
    } finally {
      setChangingDate(false);
      setUpdating(false);
    }
  };

  if (loading || updating) return <Loader />;
  if (!challenge || !date) return <Empty icon={<Swords />}>{t('no_available_challenges')}</Empty>;

  return (
    <>
      <div className={styles.challengeActions}>
        <h2>
          {withdrawing ? (
            t('sure_withdraw_challenge')
          ) : (
            <>
              Date of challenge{' '}
              {changingDate ? (
                <ChallengeDatePicker date={date} onChange={setDate} />
              ) : (
                <span>{date.format('MMM DD hh:mm A')}</span>
              )}
            </>
          )}
        </h2>
        {props.editable && (
          <div className={styles.buttons}>
            {changingDate || withdrawing ? (
              <>
                <MainButton
                  onClick={() => {
                    setChangingDate(false);
                    setWithdrawing(false);
                  }}
                  loading={loading}
                >
                  Cancel
                </MainButton>
                <MainButton
                  onClick={() => {
                    changingDate ? handleChangeDate() : handleWithdraw();
                  }}
                >
                  {t(changingDate ? 'update' : 'withdraw')}
                </MainButton>
              </>
            ) : (
              <>
                <MainButton onClick={() => setWithdrawing(true)} loading={loading}>
                  {t('withdraw')}
                </MainButton>
                <MainButton onClick={() => setChangingDate(true)}>{t('change_time')}</MainButton>
              </>
            )}
          </div>
        )}
      </div>
      <ChallengeChat
        send={props.canSendMessages ? handleSendMessage : undefined}
        messages={challenge?.talks || []}
      />
    </>
  );
}
